/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { RootStore } from 'src/core/stores/RootStore';
import { AsyncApiCallHandler } from 'src/core/types/AsyncApiCallHandler';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { getEventBus } from 'src/core/utils/eventBus';
import { getEventBusV2 } from 'src/core/utils/eventBusV2';
import { getStorage } from 'src/core/utils/getStorage';
import { getAnalyticsService } from 'src/luis/core/services/analyticsService';
import {
    analyticsMetadataSwaggerToTrainingResultMetadata,
    analyticsStatusSwaggerToAnalyticsStatus,
    endpointHitsSwaggerToEndpointMap,
    intentFaultyUtterancesSwaggerToIntentTrainingResult
} from 'src/luis/modules/analytics/mappers';
import { OpenLuisAppEvent } from 'src/luis/modules/apps/handlers/luisAppHandlerGroup';
import { AnalyticsStatus } from 'src/luis/projects/analytics/types/AnalyticsStatus';
import { AnalyticsStatusSwagger } from 'src/luis/projects/analytics/types/swagger/AnalyticsStatusSwagger';
import { LuisId } from 'src/luis/types/Resource';
import { AppVersionStore } from 'src/luis/types/stores/apps/AppVersionStore';
import { IntentStore } from 'src/luis/types/stores/intents/IntentStore';

export type AnalyticsHandlerGroup = ReturnType<typeof createAnalyticsHandlerGroup>;

/**
 * Creates a handler group of functions for the analytics to
 * be fired by the dispatcher.
 *
 * @link Check out dispatcher.md
 *
 * @param dependencies The dependencies required by this factory.
 */
export const createAnalyticsHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore } = dependencies;
    const eventBus = getEventBus();
    const eventBusV2 = getEventBusV2();

    eventBus.on('notifyTrainingSucceeded', ({ appId }) => {
        const currentAppStore = rootStore.currentLuisAppStore;

        if (currentAppStore?.id === appId) {
            currentAppStore.currentAppVersionStore?.analyticsStore.invalidate();
        }
    });

    eventBusV2.on<OpenLuisAppEvent>({
        kind: 'openConversationApp',
        callback: params => {
            if (params.eventType === 'completed') {
                getStorage('sessionStorage').removeData('analyticsCardsTracker');
                getStorage('sessionStorage').removeData('analyticsIntentsWithErrorsCardTracker');
            }
        }
    });

    /**
     * Loads the metadata of the analytics for the given version from the current app.
     *
     * @param versionId The version id to get its analytics results.
     */
    const loadMetadata = async ({ id, context }: HandlerParams<Pick<AppVersionStore, 'id'>>) => {
        id = id || rootStore.currentLuisAppStore.activeVersion;
        const analyticsService = getAnalyticsService({ context });
        const { analyticsStore } = rootStore.currentLuisAppStore.appVersionStoreHouse.findById(id);

        if (
            analyticsStore.serviceStatus === 'notStarted' ||
            analyticsStore.serviceStatus === 'outdated' ||
            analyticsStore.serviceStatus === 'failed'
        ) {
            try {
                const trainingDateSwagger = await analyticsService.getLastTrainingDate(id);
                const trainingDate = trainingDateSwagger ? new Date(trainingDateSwagger) : null;

                if (trainingDate !== null) {
                    const url = await analyticsService.getStatusOperationUrl(id);
                    await analyticsService.getStatus(url, createAnalyticsAsyncCallHandler(rootStore, id));
                }
            } catch (error) {
                throw error;
            }
        }

        if (analyticsStore.serviceStatus === 'completed') {
            const metadataSwagger = await analyticsService.getMetadata(id);

            analyticsStore.setMetadata(analyticsMetadataSwaggerToTrainingResultMetadata(metadataSwagger));
        }
    };

    /**
     * Compares the current version analytics results with the analytics results of the given version.
     *
     * @param versionId The version id to be compared with.
     */
    const compareWithVersion = async ({ id, context }: HandlerParams<Pick<AppVersionStore, 'id'>>) => {
        await loadMetadata({ id, context });

        const activeVersionAnalyticsStore = rootStore.currentLuisAppStore.currentAppVersionStore.analyticsStore;
        const { analyticsStore } = rootStore.currentLuisAppStore.appVersionStoreHouse.findById(id);

        activeVersionAnalyticsStore.setComparedVersionDifferences(analyticsStore);
    };

    /**
     * Loads the training result for the given intent only.
     *
     * @param intentId The id of the intent to be fetched.
     */
    const loadFaultyIntent = async ({ id, context }: HandlerParams<Pick<IntentStore, 'id'>>) => {
        const analyticsService = getAnalyticsService({ context });
        const { analyticsStore } = rootStore.currentLuisAppStore.appVersionStoreHouse.findById(rootStore.currentLuisAppStore.activeVersion);

        if (analyticsStore.intentsTrainingResult.has(id)) {
            return;
        }

        if (
            analyticsStore.serviceStatus === 'notStarted' ||
            analyticsStore.serviceStatus === 'outdated' ||
            analyticsStore.serviceStatus === 'failed'
        ) {
            try {
                const trainingDateSwagger = await analyticsService.getLastTrainingDate(rootStore.currentLuisAppStore.activeVersion);
                const trainingDate = trainingDateSwagger ? new Date(trainingDateSwagger) : null;

                if (trainingDate !== null) {
                    const url = await analyticsService.getStatusOperationUrl(rootStore.currentLuisAppStore.activeVersion);
                    await analyticsService.getStatus(
                        url,
                        createAnalyticsAsyncCallHandler(rootStore, rootStore.currentLuisAppStore.activeVersion)
                    );
                }
            } catch (error) {
                throw error;
            }
        }

        if (analyticsStore.serviceStatus === 'completed') {
            const faultyIntentSwagger = await analyticsService.getIntentTrainingResult(id);

            analyticsStore.addIntentTrainingResult(id, intentFaultyUtterancesSwaggerToIntentTrainingResult(faultyIntentSwagger));
        }
    };

    /**
     * Loads the history of the endpoint hits for the given duration.
     *
     * @param startDate The starting date of the history duration.
     * @param endDate The ending date of the history duration.
     * @returns A map of the date and the endpoint hit occurred in that date.
     */
    const loadEndpointHitsHistory = async ({ startDate, endDate, context }: HandlerParams<{ startDate: Date; endDate: Date }>) => {
        const analyticsService = getAnalyticsService({ context });
        const { analyticsStore } = rootStore.currentLuisAppStore.appVersionStoreHouse.findById(rootStore.currentLuisAppStore.activeVersion);

        const historySwagger = await analyticsService.getAppEndpointHitsHistory(startDate, endDate);
        analyticsStore.setAppEndpointHits(endpointHitsSwaggerToEndpointMap(historySwagger));
    };

    return {
        loadMetadata,
        loadFaultyIntent,
        compareWithVersion,
        loadEndpointHitsHistory
    };
};

/**
 * The analytics service async calls handler to get the
 * status of the analytics calculation.
 *
 * @param dependencies The dependencies of the handler.
 * @param appId The id of the application.
 */
const createAnalyticsAsyncCallHandler = (
    rootStore: RootStore,
    versionId: LuisId
): AsyncApiCallHandler<AnalyticsStatus, AnalyticsStatusSwagger> => {
    const { analyticsStore } = rootStore.currentLuisAppStore.appVersionStoreHouse.findById(versionId);

    /**
     * Determines whether the create analytics status async call service
     * should continue hitting the API or stop
     *
     * @returns boolean true if it should continue hitting the API
     */
    const shouldRetry = () => {
        const currentStatus = analyticsStore.serviceStatus;

        switch (currentStatus) {
            case 'notStarted':
            case 'outdated':
            case 'running':
                return true;
            case 'completed':
            case 'failed':
                return false;
            default:
                throw new Error('Not a valid service status type');
        }
    };

    /**
     * Handles the API call response and update the analytics store with the response values
     *
     * @param status The analytics calculation status to apply different helper function on it
     */
    const handleCall = (status: AnalyticsStatus) => {
        analyticsStore.setServiceStatus(status.status);
    };

    /**
     * Fired when the API throw an error and update the analytics store with the error state
     *
     * @param error The error object
     */
    const onError = (error: Error) => {
        analyticsStore.setServiceStatus('failed');

        throw error;
    };

    const mapApiData = analyticsStatusSwaggerToAnalyticsStatus;

    return { shouldRetry, handleCall, onError, mapApiData };
};
